import Client from 'shopify-buy';
import { SHOPIFY_CONFIG } from '@/config/shopify';

// Initialize the Shopify client
export const shopifyClient = Client.buildClient({
  domain: SHOPIFY_CONFIG.domain,
  storefrontAccessToken: SHOPIFY_CONFIG.storefrontAccessToken,
});

// Types for our cart items
export interface CartItem {
  id: string;
  productId: string;
  variantId: string;
  title: string;
  quantity: number;
  price: number;
  image?: string;
  size?: string;
  color?: string;
}

export interface Cart {
  id: string;
  items: CartItem[];
  subtotal: number;
  total: number;
  checkoutUrl: string;
}

// Helper function to create a new cart
export const createCart = async (): Promise<any> => {
  try {
    const cart = await shopifyClient.checkout.create();
    return cart;
  } catch (error) {
    console.error('Error creating cart:', error);
    throw error;
  }
};

// Helper function to add items to cart
export const addToCart = async (cartId: string, variantId: string, quantity: number = 1): Promise<any> => {
  try {
    // Handle both formats - with and without gid:// prefix
    const cleanVariantId = variantId.replace('gid://shopify/ProductVariant/', '');
    
    const lineItemsToAdd = [{
      variantId: `gid://shopify/ProductVariant/${cleanVariantId}`,
      quantity: quantity,
    }];

    const cart = await shopifyClient.checkout.addLineItems(cartId, lineItemsToAdd);
    return cart;
  } catch (error) {
    console.error('Error adding to cart:', error);
    throw error;
  }
};

// Helper function to update cart item quantity
export const updateCartItem = async (cartId: string, lineItemId: string, quantity: number): Promise<any> => {
  try {
    const lineItemsToUpdate = [{
      id: lineItemId,
      quantity: quantity,
    }];

    const cart = await shopifyClient.checkout.updateLineItems(cartId, lineItemsToUpdate);
    return cart;
  } catch (error) {
    console.error('Error updating cart item:', error);
    throw error;
  }
};

// Helper function to remove items from cart
export const removeFromCart = async (cartId: string, lineItemId: string): Promise<any> => {
  try {
    const cart = await shopifyClient.checkout.removeLineItems(cartId, [lineItemId]);
    return cart;
  } catch (error) {
    console.error('Error removing from cart:', error);
    throw error;
  }
};

// Helper function to get cart by ID
export const getCart = async (cartId: string): Promise<any> => {
  try {
    const cart = await shopifyClient.checkout.fetch(cartId);
    return cart;
  } catch (error) {
    console.error('Error fetching cart:', error);
    throw error;
  }
};

// Helper function to get product images
const getProductImage = (productName: string): string => {
  if (productName.includes('Strike')) {
    return '/products/shirts/pbx shirt 2.webp';
  } else if (productName.includes('Shield')) {
    return '/products/shirts/pbx shirt.jpeg';
  }
  return `https://via.placeholder.com/100x100/10b981/ffffff?text=${encodeURIComponent(productName.split(' ')[0])}`;
};

// Mock cart storage for development
let mockCart = {
  id: 'mock-cart-id',
  lineItems: [] as any[],
  subtotalPrice: '0.00',
  totalPrice: '0.00',
  webUrl: 'https://example.com/checkout',
};

// For testing purposes - mock Shopify responses when not connected
export const mockShopifyResponses = {
  createCart: () => {
    mockCart = {
      id: 'mock-cart-id-' + Date.now(),
      lineItems: [],
      subtotalPrice: '0.00',
      totalPrice: '0.00',
      webUrl: 'https://example.com/checkout',
    };
    return { ...mockCart };
  },
  
  addToCart: (productName: string, variantInfo: any, quantity: number = 1) => {
    const lineItemId = `mock-line-item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Check if this exact variant is already in cart
    const existingItemIndex = mockCart.lineItems.findIndex(item => 
      item.variant.id === variantInfo.shopifyVariantId
    );
    
    if (existingItemIndex >= 0) {
      // Update existing item quantity
      mockCart.lineItems[existingItemIndex].quantity += quantity;
    } else {
      // Add new item with both size and color
      const variantTitle = variantInfo.size && variantInfo.color 
        ? `${variantInfo.size} / ${variantInfo.color}`
        : variantInfo.color || variantInfo.size || 'Default';
      
      const newItem = {
        id: lineItemId,
        title: productName,
        quantity: quantity,
        variant: {
          id: variantInfo.shopifyVariantId,
          title: variantTitle,
          price: variantInfo.price.toString(),
          size: variantInfo.size || '',
          color: variantInfo.color || '',
          image: {
            src: getProductImage(productName)
          }
        }
      };
      mockCart.lineItems.push(newItem);
    }
    
    // Recalculate totals
    const subtotal = mockCart.lineItems.reduce((total, item) => 
      total + (parseFloat(item.variant.price) * item.quantity), 0
    );
    
    mockCart.subtotalPrice = subtotal.toFixed(2);
    mockCart.totalPrice = subtotal.toFixed(2);
    
    return { ...mockCart };
  },
  
  updateCartItem: (lineItemId: string, quantity: number) => {
    const itemIndex = mockCart.lineItems.findIndex(item => item.id === lineItemId);
    if (itemIndex >= 0) {
      if (quantity <= 0) {
        mockCart.lineItems.splice(itemIndex, 1);
      } else {
        mockCart.lineItems[itemIndex].quantity = quantity;
      }
      
      // Recalculate totals
      const subtotal = mockCart.lineItems.reduce((total, item) => 
        total + (parseFloat(item.variant.price) * item.quantity), 0
      );
      
      mockCart.subtotalPrice = subtotal.toFixed(2);
      mockCart.totalPrice = subtotal.toFixed(2);
    }
    
    return { ...mockCart };
  },
  
  removeFromCart: (lineItemId: string) => {
    mockCart.lineItems = mockCart.lineItems.filter(item => item.id !== lineItemId);
    
    // Recalculate totals
    const subtotal = mockCart.lineItems.reduce((total, item) => 
      total + (parseFloat(item.variant.price) * item.quantity), 0
    );
    
    mockCart.subtotalPrice = subtotal.toFixed(2);
    mockCart.totalPrice = subtotal.toFixed(2);
    
    return { ...mockCart };
  },
  
  getCart: () => {
    return { ...mockCart };
  }
};

// Environment check
export const isShopifyConfigured = () => {
  return SHOPIFY_CONFIG.storefrontAccessToken !== 'your-storefront-access-token' &&
         SHOPIFY_CONFIG.domain !== 'pbx-pickle-dev.myshopify.com' &&
         SHOPIFY_CONFIG.storefrontAccessToken.length > 10;
}; 